import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import clsx from 'clsx';
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from 'lexical';
import { useEffect, useState } from 'react';

import type { JSX } from 'react';

import { Icon } from 'components/Icon';
import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

type BlockTypesType = 'bold' | 'italic';
const BLOCK_TYPE_ICONS = {
  bold: 'Bold',
  italic: 'Italic',
};

const BlockTypes = ['bold', 'italic'];

export const BlockFormatPlugin = (): JSX.Element => {
  const t = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  const onClick = (type: BlockTypesType): void => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
  };

  useEffect(() => {
    const removeStatusListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat('bold'));
          setIsItalic(selection.hasFormat('italic'));
        }
      });
    });

    return () => {
      removeStatusListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return (
    <div className={styles['editor__toolbar-button-group']}>
      {BlockTypes.map((type) => (
        <button
          onClick={() => onClick(type as BlockTypesType)}
          key={type}
          title={type === 'bold' ? t.format.boldTooltip : t.format.italicTooltip}
          className={clsx(
            styles['editor__toolbar-button'],
            styles[`editor__toolbar-button--${type}`],
            type === 'bold' && isBold && styles['editor__toolbar-button--active'],
            type === 'italic' && isItalic && styles['editor__toolbar-button--active']
          )}
          aria-label={`${type === 'bold' ? t.aria.labels.bold : t.aria.labels.italic}`}
          aria-keyshortcuts={`${type === 'bold' ? t.aria.keyshortcuts.bold : t.aria.keyshortcuts.italic}`}
          type="button"
        >
          <Icon name={BLOCK_TYPE_ICONS[type as keyof typeof Icon]} size={24} />
        </button>
      ))}
    </div>
  );
};
